import { useEffect, useState } from "react";
import * as services from "../../services/services";
import toast, { Toaster } from "react-hot-toast";
import { Box, Chip, MenuItem, Select, Button, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { useNavigate, useLocation } from "react-router-dom";
import { Dehaze, Delete } from "@mui/icons-material";
import Swal from "sweetalert2";

export const ROLE = {
  ADMIN: 1,
  DOCTOR: 2,
};

export const ROLE_LABEL = {
  [ROLE.ADMIN]: "Admin",
  [ROLE.DOCTOR]: "สัตวแพทย์",
};

export const ROLE_COLOR = {
  [ROLE.ADMIN]: "error",
  [ROLE.DOCTOR]: "primary",
};

export default function HospitalStaffManagement({ hospitalId }) {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchUsers();
  }, []);

  // 🔥 refresh หลังกลับมาจาก create
  useEffect(() => {
    if (location.state?.refresh) {
      fetchUsers();
    }
  }, [location.state]);

  const fetchUsers = async () => {
    try {
      const res = await services.getUserByHospital(hospitalId);
      setRows(res.data.data || []);
    } catch {
      toast.error("โหลด user ทั้งหมดไม่สำเร็จ");
    }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      // optimistic update
      setRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, roleId: newRole } : row)),
      );

      await services.updateUserRole(id, newRole);

      toast.success("เปลี่ยนสิทธิ์สำเร็จ");
    } catch {
      toast.error("เปลี่ยนสิทธิ์ไม่สำเร็จ");
      fetchUsers(); // rollback
    }
  };

  const columns = [
    {
      field: "username",
      headerName: "ชื่อผู้ใช้",
      width: 150,
    },
    {
      field: "name",
      headerName: "ชื่อ",
      width: 150,
    },
    {
      field: "lastname",
      headerName: "นามสกุล",
      width: 150,
    },
    {
      field: "email",
      headerName: "อีเมล",
      width: 200,
      renderCell: (params) => params.row.username || "",
    },
    {
      field: "roleId",
      headerName: "สิทธิ์",
      width: 150,
      renderCell: (params) => {
        const role = params.value;

        return (
          <Select
            size="small"
            value={role}
            onChange={(e) =>
              handleRoleChange(params.row.id, Number(e.target.value))
            }
          >
            <MenuItem value={ROLE.ADMIN}>
              <Chip
                label={ROLE_LABEL[ROLE.ADMIN]}
                color={ROLE_COLOR[ROLE.ADMIN]}
                size="small"
              />
            </MenuItem>

            <MenuItem value={ROLE.DOCTOR}>
              <Chip
                label={ROLE_LABEL[ROLE.DOCTOR]}
                color={ROLE_COLOR[ROLE.DOCTOR]}
                size="small"
              />
            </MenuItem>
          </Select>
        );
      },
    },
    {
      field: "Detail",
      headerName: "รายละเอียด",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button
          variant="outlined"
          startIcon={<Dehaze />}
          onClick={() => handleOpenDetail(row)}
        >
          รายละเอียด
        </Button>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 80,
      headerAlign: "center",
      align: "center",
      renderCell: ({ id }) => {
        return [
          <GridActionsCellItem
            key={`${id}-delete`}
            icon={<Delete />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="error"
          />,
        ];
      },
    },
  ];

  const handleOpenDetail = (row) => {
    navigate(`/hospitals/users/${row.id}`, {
      state: { hospitalId },
    });
  };

  const handleDeleteClick = (id) => () => {
    const deletedRow = rows.find((row) => row.id === id);

    Swal.fire({
      customClass: {
        container: "my-swal",
      },
      title: "ยืนยันที่จะลบ?",
      text: "คุณจะไม่สามารถย้อนกลับสิ่งนี้ได้!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) {
        services
           .deleteUser({ id: deletedRow.userId, hospitalId })
          .then((response) => {
            console.log("User deleted successfully:", response);
            toast.success("ลบสำเร็จ!");
            Swal.fire({
              customClass: {
                container: "my-swal",
              },
              title: "ลบแล้ว!",
              text: `${deletedRow.name} ${deletedRow.lastname} ถูกลบแล้ว`,
              icon: "success",
              confirmButtonText: "ตกลง",
            });
            fetchUsers();
          })
          .catch((error) => {
            toast.error("ลบไม่สำเร็จ!");
            console.error("Error deleting user:", error);
          });
      }
    });
  };

  return (
    <Box sx={{ width: "100%", overflow: "auto" }}>
      <Toaster />

      {/* 🔥 header + ปุ่มเพิ่ม */}
      <Box
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h6">จัดการบุคลากร</Typography>

        <Button
          variant="contained"
          onClick={() =>
            navigate(`/hospitals/users/create`, {
              state: { hospitalId },
            })
          }
        >
          เพิ่มบุคลากร
        </Button>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        getRowId={(row) => row.id}
        sx={{ width: "100%" }}
      />
    </Box>
  );
}
